import React, { Component } from 'react';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import { hostname } from '../host';
import { getAccessToken } from '../auth/AuthService';
import '../theme/misanthropy.css';

import { AddIcon, ArrowBackIcon, DeleteIcon, EditIcon, UploadIcon } from './MisIcons';

function extractCharaFromPng(buffer) {
  const view = new DataView(buffer);
  let offset = 8; // skip PNG signature
  while (offset < buffer.byteLength - 12) {
    const length = view.getUint32(offset);
    const type = String.fromCharCode(
      view.getUint8(offset + 4), view.getUint8(offset + 5),
      view.getUint8(offset + 6), view.getUint8(offset + 7)
    );
    if (type === 'tEXt') {
      const bytes = new Uint8Array(buffer, offset + 8, length);
      let nullIdx = 0;
      while (nullIdx < bytes.length && bytes[nullIdx] !== 0) nullIdx++;
      const keyword = new TextDecoder().decode(bytes.slice(0, nullIdx));
      if (keyword === 'chara') {
        return atob(new TextDecoder().decode(bytes.slice(nullIdx + 1)));
      }
    }
    offset += 12 + length;
  }
  return null;
}

function mapCardToForm(card) {
  const d = (card.spec === 'chara_card_v2' && card.data) ? card.data : card;
  return {
    name: d.name || '',
    description: d.description || '',
    personality: d.personality || '',
    scenario: d.scenario || '',
    firstMes: d.first_mes || '',
    mesExample: d.mes_example || '',
    systemPrompt: d.system_prompt || '',
    postHistoryInstructions: d.post_history_instructions || '',
    creator: d.creator || '',
    creatorNotes: d.creator_notes || '',
    characterVersion: d.character_version || '',
    avatarData: ''
  };
}

const EMPTY_FORM = {
  name: '', description: '', personality: '', scenario: '',
  firstMes: '', mesExample: '', systemPrompt: '', postHistoryInstructions: '',
  creator: '', creatorNotes: '', characterVersion: '', avatarData: ''
};

class MisPersonaRoster extends Component {
  constructor(props) {
    super(props);
    this.state = {
      personas: [],
      loading: true,
      editOpen: false,
      editId: null,
      form: { ...EMPTY_FORM }
    };
    this.onKey = this.onKey.bind(this);
  }

  componentDidMount() {
    this.load();
    document.addEventListener('keydown', this.onKey);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
  }

  onKey(e) {
    if (e.key === 'Escape' && this.state.editOpen) {
      this.setState({ editOpen: false });
    }
  }

  get headers() { return { Authorization: 'Bearer ' + getAccessToken() }; }

  load() {
    axios.get(hostname() + 'rest/misanthropy/personas', { headers: this.headers })
      .then(res => this.setState({ personas: res.data, loading: false }))
      .catch(() => this.setState({ loading: false }));
  }

  openCreate() {
    this.setState({ editOpen: true, editId: null, form: { ...EMPTY_FORM } });
  }

  openEdit(persona) {
    this.setState({
      editOpen: true,
      editId: persona.id,
      form: {
        name: persona.name || '',
        description: persona.description || '',
        personality: persona.personality || '',
        scenario: persona.scenario || '',
        firstMes: persona.firstMes || '',
        mesExample: persona.mesExample || '',
        systemPrompt: persona.systemPrompt || '',
        postHistoryInstructions: persona.postHistoryInstructions || '',
        creator: persona.creator || '',
        creatorNotes: persona.creatorNotes || '',
        characterVersion: persona.characterVersion || '',
        avatarData: persona.avatarData || ''
      }
    });
  }

  save() {
    const { editId, form } = this.state;
    const req = editId
      ? axios.put(hostname() + 'rest/misanthropy/personas/' + editId, form, { headers: this.headers })
      : axios.post(hostname() + 'rest/misanthropy/personas', form, { headers: this.headers });
    req.then(() => {
      this.setState({ editOpen: false });
      this.load();
    }).catch(err => console.error(err));
  }

  delete(id) {
    if (!window.confirm('Delete this persona?')) return;
    axios.delete(hostname() + 'rest/misanthropy/personas/' + id, { headers: this.headers })
      .then(() => this.load())
      .catch(err => console.error(err));
  }

  handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const isPng = file.name.toLowerCase().endsWith('.png');
    const isJson = file.name.toLowerCase().endsWith('.json');
    if (!isPng && !isJson) return;

    if (isJson) {
      const reader = new FileReader();
      reader.onload = ev => {
        try {
          const form = mapCardToForm(JSON.parse(ev.target.result));
          this.setState({ editOpen: true, editId: null, form });
        } catch (err) { console.error('Failed to parse character card JSON', err); }
      };
      reader.readAsText(file);
    } else {
      const bufReader = new FileReader();
      bufReader.onload = ev => {
        try {
          const json = extractCharaFromPng(ev.target.result);
          if (!json) { console.error('No chara tEXt chunk found in PNG'); return; }
          const form = mapCardToForm(JSON.parse(json));
          const urlReader = new FileReader();
          urlReader.onload = ev2 => {
            this.setState({ editOpen: true, editId: null, form: { ...form, avatarData: ev2.target.result } });
          };
          urlReader.readAsDataURL(file);
        } catch (err) { console.error('Failed to parse character card from PNG', err); }
      };
      bufReader.readAsArrayBuffer(file);
    }
  }

  handleAvatarUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => this.setState(prev => ({ form: { ...prev.form, avatarData: ev.target.result } }));
    reader.readAsDataURL(file);
  }

  setForm(field, value) {
    this.setState(prev => ({ form: { ...prev.form, [field]: value } }));
  }

  renderAvatar(src, name, sizeClass, extra) {
    const initial = name ? name.charAt(0) : '?';
    return src
      ? <img className={'mis-avatar ' + sizeClass} src={src} alt={name || 'avatar'} {...extra} />
      : <span className={'mis-avatar ' + sizeClass} {...extra}>{initial}</span>;
  }

  render() {
    const { personas, loading, editOpen, editId, form } = this.state;

    return (
      <div className="mis-page">
        <div className="mis-inner">
          <div className="mis-topbar">
            <NavLink className="mis-icon-btn" to="/misanthropy" title="Back to sessions">
              <ArrowBackIcon />
            </NavLink>
            <h1 className="mis-h1">Personas</h1>
            <span className="mis-topbar-spacer" />
            <button className="mis-icon-btn" title="Import character card (.json or .card.png)"
              onClick={() => document.getElementById('persona-import-input').click()}>
              <UploadIcon />
            </button>
            <input id="persona-import-input" type="file" accept=".json,.png"
              style={{ display: 'none' }} onChange={e => this.handleImport(e)} />
          </div>

          {loading &&
            <div className="mis-loading"><div className="mis-spinner" /></div>}

          {!loading &&
            <div className="mis-grid">
              {personas.map(p => (
                <div className="mis-card" key={p.id}>
                  <div className="mis-card-head">
                    {this.renderAvatar(p.avatarData, p.name, 'mis-avatar-48')}
                    <div style={{ minWidth: 0 }}>
                      <h3 className="mis-card-title" title={p.name}>{p.name}</h3>
                      {p.creator && <p className="mis-card-sub">{p.creator}</p>}
                    </div>
                  </div>
                  <div className="mis-card-body">
                    <p className="mis-card-desc">{p.description || 'No description.'}</p>
                  </div>
                  <div className="mis-card-foot">
                    <span className="mis-foot-right">
                      <button className="mis-icon-btn mis-icon-btn-sm" title="Edit"
                        onClick={() => this.openEdit(p)}><EditIcon /></button>
                      <button className="mis-icon-btn mis-icon-btn-sm mis-danger" title="Delete"
                        onClick={() => this.delete(p.id)}><DeleteIcon /></button>
                    </span>
                  </div>
                </div>
              ))}

              {personas.length === 0 &&
                <p className="mis-empty">No personas yet. Press + to create one.</p>}
            </div>}
        </div>

        <button className="mis-fab" title="New persona" onClick={() => this.openCreate()}>
          <AddIcon />
        </button>

        {editOpen &&
          <div className="mis-backdrop" role="dialog" aria-modal="true"
            onClick={e => { if (e.currentTarget === e.target) this.setState({ editOpen: false }); }}>
            <div className="mis-modal">
              <div className="mis-modal-head">
                <h2 className="mis-modal-title">{editId ? 'Edit Persona' : 'New Persona'}</h2>
              </div>
              <div className="mis-modal-body">
                <div className="mis-uploader">
                  <div className="mis-uploader-col">
                    {this.renderAvatar(form.avatarData, form.name, 'mis-avatar-80', {
                      onClick: () => document.getElementById('persona-avatar-input').click()
                    })}
                    <input id="persona-avatar-input" type="file" accept="image/*"
                      style={{ display: 'none' }} onChange={e => this.handleAvatarUpload(e)} />
                    <p className="mis-uploader-hint">Click to upload</p>
                  </div>
                  <div className="mis-uploader-fields">
                    <div className="mis-field">
                      <label className="mis-field-label">Name *</label>
                      <input className="mis-input" value={form.name}
                        onChange={e => this.setForm('name', e.target.value)} />
                    </div>
                    <div className="mis-field">
                      <label className="mis-field-label">Creator</label>
                      <input className="mis-input" value={form.creator}
                        onChange={e => this.setForm('creator', e.target.value)} />
                    </div>
                    <div className="mis-field">
                      <label className="mis-field-label">Character Version</label>
                      <input className="mis-input" value={form.characterVersion}
                        onChange={e => this.setForm('characterVersion', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="mis-field">
                  <label className="mis-field-label">Description</label>
                  <textarea className="mis-textarea" rows={7} value={form.description}
                    onChange={e => this.setForm('description', e.target.value)} />
                  <p className="mis-help">
                    Who this person is — how they behave and speak at the table. This is the
                    only persona text the AI reads, so put the whole personality here.
                  </p>
                </div>
              </div>
              <div className="mis-modal-foot">
                <button className="mis-btn mis-btn-quiet"
                  onClick={() => this.setState({ editOpen: false })}>Cancel</button>
                <button className="mis-btn mis-btn-primary" disabled={!form.name.trim()}
                  onClick={() => this.save()}>Save</button>
              </div>
            </div>
          </div>}
      </div>
    );
  }
}

export default MisPersonaRoster;
